import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { articleApiClient } from '@src/api/tenant/article/articleApiClient';
import { mediaCategoryApiClient } from '@src/api/tenant/mediaCategory/mediaCategoryApiClient';
import { Article } from '@src/api/types/article';
import { MediaCategory } from '@src/api/types/mediaCategory';
import { Pagination } from '@src/api/types/pagination';
import { brandColors } from '@src/assets/colors/brandColors';
import PinIcon from '@src/assets/images/common/icon/PinIcon';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import MoreVertMenu from '@src/ui/elements/table/MoreVertMenu';
import Title from '@src/ui/elements/typography/Title';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import logger from '@src/utils/logger.ts';
import { useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

const ArticleListForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [articles, setArticles] = useState<Article[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [keyword, setKeyword] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<MediaCategory[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<number[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const toast = useToast();

  const loadArticles = async (
    page = 1,
    sortField: string | null = null,
    sortDirection: 'asc' | 'desc' = 'asc',
    keyword: string | null = null,
  ) => {
    try {
      const params: { page: number; sort?: string; direction?: 'asc' | 'desc'; keyword?: string } =
        { page };
      if (sortField) {
        params.sort = sortField;
        params.direction = sortDirection;
      }
      if (keyword) {
        params.keyword = keyword;
      }

      const res = await articleApiClient.index(params);
      handleApiResponse(res, (data) => {
        setArticles(data.data);
        setPagination(data.pagination);
        setFilteredCount(data.pagination?.total || 0);
        setSearchedKeyword(keyword || '');
      });
    } catch (error) {
      logger.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const page = parseInt(query.get('page') || '1', 10);
    const sort = query.get('sort') || 'published_at';
    const direction = (query.get('direction') as 'asc' | 'desc') || 'desc';
    const keyword = query.get('keyword');

    setSortField(sort);
    setSortDirection(direction);
    loadArticles(page, sort, direction, keyword);
  }, [location.search]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await mediaCategoryApiClient.index();
        handleApiResponse(res, (data) => {
          setCategories(data);
        });
      } catch (error) {
        logger.error('Failed to load categories:', error);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-button') && !target.closest('.dropdown-content')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, articleId: number) => {
    if (event.target.checked) {
      setSelectedArticles([...selectedArticles, articleId]);
    } else {
      setSelectedArticles(selectedArticles.filter((id) => id !== articleId));
    }
  };

  const handlePageChange = (page: number) => {
    const currentPage = new URLSearchParams(location.search).get('page');
    if (currentPage === page.toString()) {
      return;
    }
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    setLoading(true);
    navigate({ search: query.toString() });
  };

  const handleSortChange = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    const query = new URLSearchParams(location.search);
    query.set('sort', field);
    query.set('direction', newDirection);
    query.set('page', '1'); // ソート変更時はページを1にリセット
    setLoading(true);
    navigate({ search: query.toString() });
  };

  const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const handleKeywordSearch = () => {
    const query = new URLSearchParams(location.search);
    const currentKeyword = query.get('keyword');

    // 同じキーワードでの連続検索を防ぐ
    if (currentKeyword === keyword?.trim()) {
      return;
    }

    setLoading(true);
    query.set('keyword', keyword || '');
    query.set('page', '1'); // キーワード検索時はページを1にリセット
    navigate({ search: query.toString() });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleKeywordSearch();
    }
  };

  const handleCategoryChange = async (categoryId: number) => {
    setLoading(true);
    try {
      await articleApiClient.bulkUpdateCategory({
        article_ids: selectedArticles,
        media_category_id: categoryId,
      });
      await loadArticles(pagination?.current_page || 1, sortField, sortDirection, keyword);
      setSelectedArticles([]);
      toast(TOAST_MESSAGE.UPDATE);
    } catch (error) {
      logger.error('Failed to update categories:', error);
    } finally {
      setLoading(false);
      setIsDropdownOpen(false);
    }
  };

  const openDeleteModal = (articleId: number) => {
    setSelectedArticleId(articleId);
    setIsOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedArticleId(null);
  };

  const confirmDeleteArticle = async () => {
    if (!selectedArticleId) {
      return;
    }
    setLoading(true);
    try {
      await articleApiClient.delete(selectedArticleId);
      const currentPage = new URLSearchParams(location.search).get('page') || '1';
      await loadArticles(parseInt(currentPage, 10), sortField, sortDirection, keyword);
      toast(TOAST_MESSAGE.DELETE);
    } catch (error) {
      logger.error('Failed to delete article:', error);
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  return (
    <main className='grow'>
      <SpinnerOverlay loading={loading} />
      <div className=''>
        <div className='flex items-center justify-between'>
          <Title>記事一覧</Title>
          <div className='w-[250px]'>
            <InputSearch
              key={searchedKeyword}
              value={keyword}
              defaultValue={searchedKeyword}
              onChange={handleKeywordChange}
              onKeyDown={handleKeyDown}
              onSubmit={handleKeywordSearch}
              onClear={() => {
                setKeyword('');
                navigate({ search: '' });
              }}
            />
          </div>
        </div>
        <div className='my-4 flex items-center justify-between'>
          <div className='w-[172px]'>
            <div className='relative'>
              {selectedArticles.length > 0 && (
                <button
                  className='placeholder:text-custom dropdown-button flex w-full cursor-pointer appearance-none items-center justify-between gap-4 rounded border border-solid border-primary-500 px-5 py-[10px] text-sm text-primary-500 focus:outline-none'
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span>カテゴリの変更</span>
                  <div className='h-[5px] w-[10px] bg-primary-500 [clip-path:polygon(50%_100%,0_0,100%_0)]'></div>
                </button>
              )}
              {isDropdownOpen && (
                <div className='dropdown-content absolute left-0 top-[102%] z-10 w-[240px]'>
                  <div className='flex flex-col gap-px overflow-hidden rounded border border-solid border-gray-200 bg-gray-200'>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        type='button'
                        className='block select-none bg-white px-6 py-3 text-left text-sm transition-colors hover:bg-primary-500 hover:text-white'
                        onClick={() => handleCategoryChange(category.id)}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Button
            size='s'
            onClick={() => {
              navigate('/tenant/articles/create');
            }}
          >
            新規作成
          </Button>
        </div>
        {searchedKeyword && (
          <p className='overflow-wrap-anywhere mt-8 text-sm'>
            「{searchedKeyword}」で絞り込んだ結果：{filteredCount}件
          </p>
        )}
        <div className='mt-8 overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[42px]' />
              <col className='w-[132px]' />
              <col className='w-[24px]' />
              <col className='w-full' />
              <col className='w-[132px]' />
              <col className='w-[144px]' />
              <col className='w-[144px]' />
              <col className='w-[46px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <th className='h-[44px]'>
                  <label className='grid size-full cursor-pointer place-items-center border-r border-solid border-r-gray-200'>
                    <input
                      type='checkbox'
                      // 今表示されている記事が全て選択されているか
                      checked={
                        articles.length > 0 &&
                        articles.every((article) => selectedArticles.includes(article.id))
                      }
                      onChange={(e) => {
                        const currentArticleIds = articles.map((article) => article.id);
                        if (e.target.checked) {
                          // 現在表示されているページの記事をすべて選択
                          setSelectedArticles((prevSelected) => [
                            ...new Set([...prevSelected, ...currentArticleIds]),
                          ]);
                        } else {
                          // 現在表示されているページの記事の選択を解除
                          setSelectedArticles((prevSelected) =>
                            prevSelected.filter((id) => !currentArticleIds.includes(id)),
                          );
                        }
                      }}
                    />
                  </label>
                </th>
                <th>
                  <button
                    type='button'
                    className={`flex w-full cursor-pointer items-center gap-5 px-3 py-2 text-xs font-semibold ${sortField === 'published_at' ? 'after:opacity-100' : ''}`}
                    onClick={() => handleSortChange('published_at')}
                  >
                    <span>公開日</span>
                    <div className='flex flex-col'>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_0,0_100%,100%_100%)] ${sortField === 'published_at' && sortDirection === 'asc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_100%,0_0,100%_0)] ${sortField === 'published_at' && sortDirection === 'desc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                    </div>
                  </button>
                </th>
                <th></th>
                <th>
                  <span className='flex w-full items-center gap-5 px-3 py-2 text-xs font-semibold'>
                    記事タイトル
                  </span>
                </th>
                <th>
                  <button
                    type='button'
                    className={`flex w-full cursor-pointer items-center gap-5 px-3 py-2 text-xs font-semibold ${sortField === 'tenantUser.fullname_kana' ? 'after:opacity-100' : ''}`}
                    onClick={() => handleSortChange('tenantUser.fullname_kana')}
                  >
                    <span>作成者</span>
                    <div className='flex flex-col'>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_0,0_100%,100%_100%)] ${sortField === 'tenantUser.fullname_kana' && sortDirection === 'asc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_100%,0_0,100%_0)] ${sortField === 'tenantUser.fullname_kana' && sortDirection === 'desc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                    </div>
                  </button>
                </th>
                <th>
                  <button
                    type='button'
                    className={`flex w-full cursor-pointer items-center gap-5 px-3 py-2 text-xs font-semibold ${sortField === 'media_category_id' ? 'after:opacity-100' : ''}`}
                    onClick={() => handleSortChange('media_category_id')}
                  >
                    <span>カテゴリ</span>
                    <div className='flex flex-col'>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_0,0_100%,100%_100%)] ${sortField === 'media_category_id' && sortDirection === 'asc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_100%,0_0,100%_0)] ${sortField === 'media_category_id' && sortDirection === 'desc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                    </div>
                  </button>
                </th>
                <th>
                  <button
                    type='button'
                    className={`flex w-full cursor-pointer items-center gap-5 px-3 py-2 text-xs font-semibold ${sortField === 'status' ? 'after:opacity-100' : ''}`}
                    onClick={() => handleSortChange('status')}
                  >
                    <span>ステータス</span>
                    <div className='flex flex-col'>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_0,0_100%,100%_100%)] ${sortField === 'status' && sortDirection === 'asc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                      <div
                        className={`grid size-[12px] place-items-center after:h-[4px] after:w-[8px] after:bg-black after:[clip-path:polygon(50%_100%,0_0,100%_0)] ${sortField === 'status' && sortDirection === 'desc' ? 'after:opacity-100' : 'after:opacity-20'} after:transition-opacity hover:after:opacity-80`}
                      ></div>
                    </div>
                  </button>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {articles.length > 0
                ? articles.map((article) => (
                    <tr
                      key={article.id}
                      className='[&:nth-child(2n)]:bg-gray-10'
                    >
                      <th>
                        <label className='grid size-full cursor-pointer place-items-center border-r border-solid border-r-gray-200'>
                          <input
                            type='checkbox'
                            checked={selectedArticles.includes(article.id)}
                            onChange={(e) => handleCheckboxChange(e, article.id)}
                          />
                        </label>
                      </th>
                      <td>
                        <div className='px-4 py-6'>{article.published_at}</div>
                      </td>
                      <td>
                        <div className='flex items-center justify-center py-6'>
                          {article.is_top_display && (
                            <PinIcon
                              color={brandColors['500']}
                              size={20}
                            />
                          )}
                        </div>
                      </td>
                      <td title={article.title}>
                        <div className='truncate px-4 py-6'>{article.title}</div>
                      </td>
                      <td
                        title={`${article.tenant_user.family_name} ${article.tenant_user.first_name}`}
                      >
                        <div className='truncate px-4 py-6'>{`${article.tenant_user.family_name} ${article.tenant_user.first_name}`}</div>
                      </td>
                      <td title={article.category?.name}>
                        <div className='truncate px-4 py-6'>{article.category?.name}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{article.status}</div>
                      </td>
                      <td>
                        <div className='grid h-full place-items-center'>
                          <MoreVertMenu
                            menuItems={[
                              {
                                label: '編集する',
                                onClick: () => {
                                  navigate(
                                    generatePath(TENANT_URL.ARTICLES_EDIT, { id: article.id }),
                                  );
                                },
                              },
                              {
                                label: '削除する',
                                variant: 'warning',
                                onClick: () => openDeleteModal(article.id),
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {!searchedKeyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
          <ConfirmModal
            title='完全に削除しますか？'
            message='記事が完全に削除されますがよろしいですか？'
            confirmText='完全に削除'
            cancelText='戻る'
            isOpen={isOpenDeleteModal}
            onConfirm={confirmDeleteArticle}
            onClose={closeDeleteModal}
            isDanger={true}
          />
        </div>

        {/* ページング */}
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <div className='pb-20'></div>
    </main>
  );
};

export default ArticleListForm;
